import { cookies } from "next/headers"
import { NextResponse } from "next/server"
import { z } from "zod"
import { getSession } from "@/services/auth-service"
import {
  applyWooCartSessionCookies,
  buildForwardCookieHeader,
  readWooGuestSessionCookies,
} from "@/services/cart-service"
import { fetchCheckoutOrderSummary } from "@/services/checkout-service"

const NO_CACHE_HEADERS = {
  "Cache-Control": "no-store",
} as const

const shippingAddressPayloadSchema = z.object({
  shippingAddress: z
    .object({
      country: z.string().trim().min(1),
      state: z.string().trim().min(1),
      city: z.string().trim(),
      postcode: z.string().trim(),
    })
    .optional(),
})

const getForwardedCookieHeader = async (): Promise<string> => {
  const session = await getSession()
  const cookieStore = await cookies()

  return buildForwardCookieHeader({
    authSessionToken: session?.wordpressSessionCookie ?? null,
    wooGuestSessionCookies: readWooGuestSessionCookies(cookieStore),
  })
}

export const POST = async (request: Request) => {
  const rawPayload = await request.text()
  const payload = (() => {
    if (!rawPayload.trim()) {
      return {}
    }

    try {
      return JSON.parse(rawPayload) as unknown
    } catch {
      return null
    }
  })()
  const parsedPayload = shippingAddressPayloadSchema.safeParse(payload)

  if (!parsedPayload.success) {
    return NextResponse.json(
      {
        ok: false,
        code: "INVALID_PAYLOAD",
        message: "Payload invalido para sincronizar o resumo do checkout.",
      },
      { status: 422, headers: NO_CACHE_HEADERS }
    )
  }

  try {
    const forwardedCookieHeader = await getForwardedCookieHeader()
    const result = await fetchCheckoutOrderSummary(
      forwardedCookieHeader,
      parsedPayload.data.shippingAddress
    )
    const response = NextResponse.json(
      {
        ok: result.ok,
        reload: result.reload,
        totals: result.totals,
        shippingMethods: result.shippingMethods,
        paymentInstallments: result.paymentInstallments,
        selectedShippingMethod: result.selectedShippingMethod,
      },
      { headers: NO_CACHE_HEADERS }
    )

    applyWooCartSessionCookies(response, result.upstreamSetCookie)

    return response
  } catch (error) {
    return NextResponse.json(
      {
        ok: false,
        code:
          error instanceof Error
            ? error.message
            : "CHECKOUT_ORDER_REVIEW_FAILED",
        message: "Nao foi possivel sincronizar o resumo do checkout.",
      },
      { status: 502, headers: NO_CACHE_HEADERS }
    )
  }
}
